import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapView, type MapViewHandle } from './components/MapView';
import { PointList } from './components/PointList';
import { SearchBox } from './components/SearchBox';
import { PlacesIO } from './components/PlacesIO';
import { Legend } from './components/Legend';
import { fetchMeta, fetchStations, reverseGeocode } from './lib/api';
import { QUALITY_PRESETS, makeGrid, type QualityId } from './lib/grid';
import { useFieldEngine, type FieldRequest } from './lib/useFieldEngine';
import { BAND_MINUTES, FINE_BAND_MINUTES, formatMinutes } from './lib/palette';
import { nextPointId, readInitialPlaces, savePlaces } from './lib/places';
import type { DisplayMode, GeocodeResult, Meta, Point, Stations, ViewMode } from './lib/types';

const PROBE_ID = 'probe';

/** A few well-known places, for the empty state's example button. */
const EXAMPLE_PLACES = [
  { label: 'Alexanderplatz', lat: 52.52196, lon: 13.41364, weight: 6 },
  { label: 'Zoologischer Garten', lat: 52.50726, lon: 13.33256, weight: 4 },
  { label: 'Tempelhofer Feld', lat: 52.4757, lon: 13.4029, weight: 3 },
];

/*
 * Read once, at module load rather than per render: adopting a shared link
 * consumes the fragment, and React would run a state initialiser twice.
 */
const INITIAL_POINTS = readInitialPlaces();

const vectorKey = (lat: number, lon: number, dir: 'to' | 'from') =>
  `${lat.toFixed(5)},${lon.toFixed(5)}:${dir}`;

export default function App() {
  const [meta, setMeta] = useState<Meta | null>(null);
  const [stations, setStations] = useState<Stations | null>(null);
  const [bootError, setBootError] = useState<string | null>(null);

  const [points, setPoints] = useState<Point[]>(INITIAL_POINTS);
  const [viewMode, setViewMode] = useState<ViewMode>({ kind: 'combined' });
  const [pickingMode, setPickingMode] = useState<'add' | 'explore'>('add');

  const [quality, setQuality] = useState<QualityId>('balanced');
  const [display, setDisplay] = useState<DisplayMode>('bands10');
  const [contours, setContours] = useState(true);
  const [opacity, setOpacity] = useState(0.82);
  const [showStops, setShowStops] = useState(true);
  const [showLines, setShowLines] = useState(true);
  const [panelOpen, setPanelOpen] = useState(true);

  const mapRef = useRef<MapViewHandle | null>(null);

  // ---- boot
  useEffect(() => {
    Promise.all([fetchMeta(), fetchStations()])
      .then(([m, s]) => {
        setMeta(m);
        setStations(s);
      })
      .catch((err) => setBootError(err instanceof Error ? err.message : String(err)));
  }, []);

  // This browser's copy of the list. Weights and labels count as edits too.
  useEffect(() => {
    savePlaces(points);
  }, [points]);

  const cellM = QUALITY_PRESETS.find((q) => q.id === quality)!.cellM;
  const grid = useMemo(() => (meta ? makeGrid(meta.bbox, cellM) : null), [meta, cellM]);

  const onTexture = useCallback((data: Uint16Array, width: number, height: number) => {
    mapRef.current?.setField(data, width, height);
  }, []);
  const onEmpty = useCallback(() => mapRef.current?.clearField(), []);

  const engine = useFieldEngine({
    meta,
    stations,
    grid: grid ?? makeGrid({ minLat: 52.3, maxLat: 52.7, minLon: 13.0, maxLon: 13.8 }, cellM),
    onTexture,
    onEmpty,
  });

  // ---- what to compute
  const requests = useMemo<FieldRequest[]>(() => {
    if (viewMode.kind === 'explore') {
      const o = viewMode.origin;
      return [{ key: vectorKey(o.lat, o.lon, 'from'), lat: o.lat, lon: o.lon, weight: 1, dir: 'from' }];
    }
    return points
      .filter((p) => p.weight > 0)
      .map((p) => ({
        key: vectorKey(p.lat, p.lon, 'to'),
        lat: p.lat,
        lon: p.lon,
        weight: p.weight,
        dir: 'to' as const,
      }));
  }, [points, viewMode]);

  // Recompute only when the inputs that matter change — renaming a place must not
  // trigger a rebuild.
  const signature = requests.map((r) => `${r.key}@${r.weight}`).join('|');
  const requestsRef = useRef(requests);
  requestsRef.current = requests;
  const { request } = engine;
  useEffect(() => {
    if (!grid) return;
    request(requestsRef.current);
  }, [signature, grid, request]);

  // ---- point editing
  const addPoint = useCallback((lat: number, lon: number, label?: string) => {
    const id = nextPointId();
    setPoints((prev) => [
      ...prev,
      { id, lat, lon, label: label ?? '', weight: 5, resolving: !label },
    ]);
    setViewMode({ kind: 'combined' });
    if (!label) {
      reverseGeocode(lat, lon)
        .then((r) => {
          setPoints((prev) =>
            prev.map((p) =>
              p.id === id
                ? { ...p, label: r.label || `${lat.toFixed(4)}, ${lon.toFixed(4)}`, resolving: false }
                : p
            )
          );
        })
        .catch(() => {
          setPoints((prev) =>
            prev.map((p) =>
              p.id === id ? { ...p, label: `${lat.toFixed(4)}, ${lon.toFixed(4)}`, resolving: false } : p
            )
          );
        });
    }
  }, []);

  const onMapClick = useCallback(
    (lat: number, lon: number) => {
      if (pickingMode === 'explore') {
        setViewMode({ kind: 'explore', origin: { id: PROBE_ID, lat, lon, label: 'this spot' } });
        reverseGeocode(lat, lon)
          .then((r) => {
            if (!r.label) return;
            setViewMode((v) =>
              v.kind === 'explore' && v.origin.id === PROBE_ID
                ? { kind: 'explore', origin: { ...v.origin, label: r.label! } }
                : v
            );
          })
          .catch(() => {});
        return;
      }
      addPoint(lat, lon);
    },
    [pickingMode, addPoint]
  );

  const onPickAddress = useCallback(
    (r: GeocodeResult) => {
      addPoint(r.lat, r.lon, r.label);
      mapRef.current?.flyTo(r.lat, r.lon);
    },
    [addPoint]
  );

  const importPoints = useCallback((imported: Point[]) => {
    setPoints(imported);
    setViewMode({ kind: 'combined' });
  }, []);

  const setLabel = useCallback((id: string, label: string) => {
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, label, resolving: false } : p)));
    setViewMode((v) =>
      v.kind === 'explore' && v.origin.id === id ? { kind: 'explore', origin: { ...v.origin, label } } : v
    );
  }, []);

  const setWeight = useCallback((id: string, weight: number) => {
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, weight } : p)));
  }, []);

  const removePoint = useCallback((id: string) => {
    setPoints((prev) => prev.filter((p) => p.id !== id));
    setViewMode((v) => (v.kind === 'explore' && v.origin.id === id ? { kind: 'combined' } : v));
  }, []);

  const movePoint = useCallback((id: string, lat: number, lon: number) => {
    setPoints((prev) => prev.map((p) => (p.id === id ? { ...p, lat, lon } : p)));
    setViewMode((v) =>
      v.kind === 'explore' && v.origin.id === id
        ? { kind: 'explore', origin: { ...v.origin, lat, lon } }
        : v
    );
  }, []);

  /** Clicking a pin shows times outward from it; clicking it again goes back. */
  const toggleExplore = useCallback(
    (id: string) => {
      if (viewMode.kind === 'explore' && viewMode.origin.id === id) {
        setViewMode({ kind: 'combined' });
        return;
      }
      const p = points.find((x) => x.id === id);
      if (!p) {
        setViewMode({ kind: 'combined' });
        return;
      }
      setViewMode({
        kind: 'explore',
        origin: { id, lat: p.lat, lon: p.lon, label: p.label || 'this place' },
      });
    },
    [points, viewMode]
  );

  const focusPoint = useCallback(
    (id: string) => {
      const p = points.find((x) => x.id === id);
      if (p) mapRef.current?.flyTo(p.lat, p.lon);
    },
    [points]
  );

  const exploringId = viewMode.kind === 'explore' ? viewMode.origin.id : null;
  const heatmapStyle = useMemo(() => ({ display, contours, opacity }), [display, contours, opacity]);

  const caption =
    viewMode.kind === 'explore'
      ? `Travel time from ${viewMode.origin.label}`
      : points.length === 1
        ? `Travel time to ${points[0].label || 'your place'}`
        : `Weighted average travel time to ${points.length} places`;

  if (bootError) {
    return (
      <div className="boot boot--error">
        <h1>Lowcation</h1>
        <p>Could not load the travel-time index.</p>
        <code>{bootError}</code>
        <p className="boot__hint">
          Start the API with <code>npm run dev:server</code>, and build the index first with{' '}
          <code>npm run data:build</code> if you haven't.
        </p>
      </div>
    );
  }

  if (!meta || !stations || !grid) {
    return (
      <div className="boot">
        <div className="boot__mark">Lowcation</div>
        <div className="boot__bar">
          <span />
        </div>
        <p>Loading the Berlin transit index…</p>
      </div>
    );
  }

  return (
    <div className={`app${panelOpen ? '' : ' app--collapsed'}`}>
      <MapView
        ref={mapRef}
        grid={grid}
        stations={stations}
        points={points}
        viewMode={viewMode}
        values={engine.result?.values ?? null}
        heatmapStyle={heatmapStyle}
        showStops={showStops}
        showLines={showLines}
        pickingMode={pickingMode}
        onMapClick={onMapClick}
        onPointClick={toggleExplore}
        onPointMoved={movePoint}
      />

      <button
        type="button"
        className="panel-toggle"
        aria-expanded={panelOpen}
        title={panelOpen ? 'Hide the panel' : 'Show the panel'}
        onClick={() => setPanelOpen((v) => !v)}
      >
        {panelOpen ? '‹' : '›'}
      </button>

      <aside className="panel">
        <header className="panel__head">
          <h1 className="brand">
            Low<span>cation</span>
          </h1>
          <p className="tagline">
            Pick the places you go. See where in Berlin they are in reach.
          </p>
        </header>

        <div className="panel__body">
          <SearchBox stations={stations} onPick={onPickAddress} />

          <div className="hint">
            {pickingMode === 'add' ? (
              <>Click the map to drop a place, or search above. Drag pins to fine-tune.</>
            ) : (
              <>Click anywhere to see travel times <em>from</em> that spot.</>
            )}
          </div>

          {points.length === 0 && viewMode.kind === 'combined' && (
            <div className="empty">
              <p>No places yet.</p>
              <button
                type="button"
                className="btn btn--primary"
                onClick={() => {
                  const created = EXAMPLE_PLACES.map((e) => ({
                    id: nextPointId(),
                    lat: e.lat,
                    lon: e.lon,
                    label: e.label,
                    weight: e.weight,
                  }));
                  setPoints(created);
                }}
              >
                Try an example
              </button>
            </div>
          )}

          {points.length > 0 && (
            <>
              <div className="section-head">
                <h2>Your places</h2>
                <button type="button" className="link" onClick={() => setPoints([])}>
                  Clear all
                </button>
              </div>
              <PointList
                points={points}
                exploringId={exploringId}
                onLabel={setLabel}
                onWeight={setWeight}
                onRemove={removePoint}
                onExplore={toggleExplore}
                onFocus={focusPoint}
              />
            </>
          )}

          <PlacesIO points={points} onImport={importPoints} />

          {viewMode.kind === 'explore' && (
            <div className="explore-note">
              <div>
                Showing times <strong>from {viewMode.origin.label}</strong> to everywhere else.
              </div>
              <button type="button" className="btn" onClick={() => setViewMode({ kind: 'combined' })}>
                Back to combined view
              </button>
            </div>
          )}

          <div className="section-head">
            <h2>Display</h2>
          </div>

          <div className="controls">
            <div className="control">
              <span className="control__label">Mode</span>
              <div className="segmented">
                <button
                  type="button"
                  className={pickingMode === 'add' ? 'is-on' : ''}
                  onClick={() => setPickingMode('add')}
                >
                  Add places
                </button>
                <button
                  type="button"
                  className={pickingMode === 'explore' ? 'is-on' : ''}
                  onClick={() => setPickingMode('explore')}
                >
                  Explore from…
                </button>
              </div>
            </div>

            <div className="control">
              <span className="control__label">Color</span>
              <div className="segmented">
                <button
                  type="button"
                  className={display === 'bands10' ? 'is-on' : ''}
                  title={`Isochrone bands ${BAND_MINUTES} minutes wide`}
                  onClick={() => setDisplay('bands10')}
                >
                  {BAND_MINUTES} min
                </button>
                <button
                  type="button"
                  className={display === 'bands5' ? 'is-on' : ''}
                  title={`Finer ${FINE_BAND_MINUTES}-minute bands, for detail within a neighbourhood`}
                  onClick={() => setDisplay('bands5')}
                >
                  {FINE_BAND_MINUTES} min
                </button>
                <button
                  type="button"
                  className={display === 'smooth' ? 'is-on' : ''}
                  onClick={() => setDisplay('smooth')}
                >
                  Smooth
                </button>
              </div>
            </div>

            <div className="control">
              <span className="control__label">Resolution</span>
              <div className="segmented">
                {QUALITY_PRESETS.map((q) => (
                  <button
                    key={q.id}
                    type="button"
                    className={quality === q.id ? 'is-on' : ''}
                    title={`${q.cellM} m cells`}
                    onClick={() => setQuality(q.id)}
                  >
                    {q.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="control control--row">
              <span className="control__label">Overlay opacity</span>
              <input
                type="range"
                min={0.25}
                max={1}
                step={0.01}
                value={opacity}
                onChange={(e) => setOpacity(Number(e.target.value))}
              />
            </label>

            <div className="checks">
              <label>
                <input type="checkbox" checked={contours} onChange={(e) => setContours(e.target.checked)} />
                Contour lines
              </label>
              <label>
                <input type="checkbox" checked={showStops} onChange={(e) => setShowStops(e.target.checked)} />
                Transit stops
              </label>
              <label title="Draw the S-Bahn network to orient yourself">
                <input type="checkbox" checked={showLines} onChange={(e) => setShowLines(e.target.checked)} />
                <span className="swatch-line" aria-hidden="true" />
                S-Bahn
              </label>
            </div>
          </div>

          {engine.error && (
            <div className="error" role="alert">
              {engine.error}
            </div>
          )}
        </div>

        <footer className="panel__foot">
          <div className="stats">
            <span title="Heatmap grid">
              {grid.width}×{grid.height} · {Math.round(grid.cellM)} m
            </span>
            {engine.result && (
              <>
                <span title="Share of the grid reachable from every place">
                  {Math.round(engine.result.stats.coverage * 100)}% covered
                </span>
                <span title="Time to rasterise the field">{engine.result.computeMs.toFixed(0)} ms</span>
              </>
            )}
            {engine.computing && <span className="stats__busy">computing…</span>}
          </div>
          {engine.result && (
            <div className="stats stats--range">
              <span>
                best {formatMinutes(engine.result.stats.minMinutes)} · worst{' '}
                {formatMinutes(engine.result.stats.maxMinutes)}
              </span>
            </div>
          )}
          <div className="provenance">
            {meta.nStations.toLocaleString()} stops · VBB timetable for{' '}
            {meta.serviceDate.slice(6, 8)}.{meta.serviceDate.slice(4, 6)}.{meta.serviceDate.slice(0, 4)} ·
            departures {String(Math.floor(meta.departures[0] / 3600)).padStart(2, '0')}:00–
            {String(Math.floor(meta.departures[meta.departures.length - 1] / 3600)).padStart(2, '0')}:00
          </div>
        </footer>
      </aside>

      {(engine.result || engine.computing) && <Legend display={display} caption={caption} />}
    </div>
  );
}
